// Copyright 2026, Imprivata, Inc.  All rights reserved.

import ReadOnlyCredentialRow from '@components/readOnlyCredentialRow/ReadOnlyCredentialRow';
import { Select } from '@imprivata-cloud/components';
import { Input } from '@imprivata-cloud/design-system';
import { Collapse, Form } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import { isValidUrl } from '../utils/validation';
import DownloadCertButton from './DownloadCertButton';
import SettingsToggle from './SettingsToggle';

const TRANSLATION_ROOT = 'apps.app-modal';
const SELECT_POPUP_CLASS = 'hyperdrive-select-dropdown';

export enum SamlFieldNames {
  AppUsernameAdMatch = 'appUsernameAdMatch',
  InterconnectUrl = 'interconnectUrl',
}

interface InterconnectUrlInputProps extends Omit<React.ComponentProps<typeof Input>, 'error' | 'errorMessage'> {
  label: string;
}

const InterconnectUrlInput: React.FC<InterconnectUrlInputProps> = (props) => {
  const { errors } = Form.Item.useStatus();
  const message = errors[0];
  return <Input {...props} error={!!message} errorMessage={message ? String(message) : undefined} />;
};

interface SamlConfigSectionProps {
  isEdit: boolean;
  idpIssuer: string | null;
  redirectUri: string;
  idpCertificate: string;
  tenantId: string;
}

const SamlConfigSection: React.FC<SamlConfigSectionProps> = ({
  isEdit,
  idpIssuer,
  redirectUri,
  idpCertificate,
  tenantId,
}) => {
  const { t } = useTranslation();
  const appUsernameAdMatch = Form.useWatch(SamlFieldNames.AppUsernameAdMatch);

  const mappingRuleOptions = [
    { value: true, label: t(`${TRANSLATION_ROOT}.mapping-rule.match-true`) },
    { value: false, label: t(`${TRANSLATION_ROOT}.mapping-rule.match-false`) },
  ] as unknown as DefaultOptionType[];

  return (
    <Collapse
      ghost
      expandIconPosition="end"
      expandIcon={({ isActive }) => <SettingsToggle isActive={isActive} />}
      className="saml-config-section"
      defaultActiveKey={isEdit ? [] : ['saml']}
      items={[
        {
          key: 'saml',
          label: <strong className="h5 section-title">{t(`${TRANSLATION_ROOT}.saml-config-title`)}</strong>,
          // Keep the panel mounted even while collapsed: `appUsernameAdMatch`
          // and `interconnectUrl` are registered on fields rendered here,
          // and AntD Form only tracks values for mounted Form.Items.
          // Without forceRender, submitting while collapsed would send
          // `undefined` for these and wipe saved SAML settings.
          forceRender: true,
          children: (
            <>
              <Form.Item name={SamlFieldNames.AppUsernameAdMatch}>
                <Select
                  label={t(`${TRANSLATION_ROOT}.mapping-rule.label`)}
                  aria-label="username-mapping-rule"
                  options={mappingRuleOptions}
                  classNames={{ popup: { root: SELECT_POPUP_CLASS } }}
                />
              </Form.Item>

              {appUsernameAdMatch === false && (
                <Form.Item
                  name={SamlFieldNames.InterconnectUrl}
                  className="interconnect-url-item"
                  validateTrigger="onBlur"
                  help={false}
                  rules={[
                    {
                      validator: (_rule, value: string | undefined) => {
                        const isEmpty = !value || value.trim() === '';
                        if (isEmpty) {
                          return Promise.reject(
                            new Error(t(`${TRANSLATION_ROOT}.errors.interconnect-base-urls-required`)),
                          );
                        }
                        if (!isValidUrl(value)) {
                          return Promise.reject(
                            new Error(t(`${TRANSLATION_ROOT}.errors.interconnect-base-urls-invalid`)),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InterconnectUrlInput
                    label={t(`${TRANSLATION_ROOT}.interconnect-url.label`)}
                    aria-label="interconnect-url-input"
                    autoFocus={!isEdit}
                  />
                </Form.Item>
              )}

              <p className="saml-config-copy">{t(`${TRANSLATION_ROOT}.saml-config-copy`)}</p>

              <div className="saml-cert-block">
                <div className="saml-cert-label">
                  {t(`${TRANSLATION_ROOT}.signing-certificate-label`)}
                </div>
                <DownloadCertButton idpCertificate={idpCertificate} tenantId={tenantId} />
              </div>

              <ReadOnlyCredentialRow
                label={t(`${TRANSLATION_ROOT}.entity-id-label`)}
                value={idpIssuer ?? ''}
                ariaLabel="saml-entity-id"
              />

              <ReadOnlyCredentialRow
                label={t(`${TRANSLATION_ROOT}.acs-url-label`)}
                value={redirectUri}
                ariaLabel="saml-redirect-url"
              />
            </>
          ),
        },
      ]}
    />
  );
};

export default SamlConfigSection;
